import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import QuillEditor from '../components/QuillEditor'
import UpgradePopup from '../components/UpgradePopup'
import {
  getJournal,
  updateJournal,
  adjustBookJournalsCount,
} from '../actions/journal'
import LoadingCard from '../components/LoadingCard'
import DateTimePicker from '../utils/DateTimePicker'
import isPaidUser from '../utils/isPaidUser'
import { createSaveQueue } from '../utils/saveQueue'
import { refusalOf, MEMBERS_ONLY, publishFailedMessage } from '../utils/refusal'
import { countWords } from '../components/QuillEditor'

// currentJournal only when it is the journal in the URL: a save that finished after leaving
// another page can leave that journal in the store, and editing it here would write its text
// into this journal (review of PR 12).
const journalFor = (props) => {
  const current = props.journal.currentJournal
  return current && current.id === props.match.params.journal_uid ? current : null
}

class EditJournal extends Component {
  _isMounted = false

  constructor(props) {
    super(props)
    const currentJournal = journalFor(props)
    this.state = {
      privacy: currentJournal ? currentJournal.privacy : 'PRIVATE',
      markedAs: currentJournal ? currentJournal.markedAs : null,
      journalUid: props.match.params.journal_uid,
      journalAuthorId:
        props.user.accountData.idToken.payload['cognito:username'],
      journalContent: currentJournal ? currentJournal.content : '',
      journalWordCount: currentJournal ? currentJournal.journalWordCount : 0,
      journalLovedCount: currentJournal ? currentJournal.lovedCount : 0,
      journalCommentCount: currentJournal ? currentJournal.commentCount : 0,
      journalWordCountBeforeEditing: currentJournal
        ? currentJournal.journalWordCount
        : 0,
      showUpgrade: false,
      journalBookId: currentJournal
        ? currentJournal.journalBookId || currentJournal.publicJournalBookId
        : '',
      bookChangedFromId: currentJournal
        ? currentJournal.journalBookId || currentJournal.publicJournalBookId
        : '',
      journalCreatedAt: currentJournal
        ? new Date(currentJournal.createdAt)
        : '',
      upgradePopUpMessage: {
        featureName: 'Publishing a public journal',
        ctaText: 'Publish it as a private journal',
      },
      isSavingJournal: false,
      saveError: '',
    }
    this.editor = React.createRef()
    // Saves run one at a time; see utils/saveQueue.js (review D1).
    this.saves = createSaveQueue((err) => {
      console.log(err)
      this.setState({ saveError: "Your draft couldn't be saved. Check your connection; it saves again as you type." })
    })
    // The newest text from the editor, outside state so a save always reads it.
    this.latest = null
    this.publishing = false
  }

  async componentDidMount() {
    this._isMounted = true
    // TODO: check logged-in userID and journal author ID are match
    // if not this.props.history.goBack()

    // set focus to the bottom of the screen
    window.scrollBy(0, window.innerHeight + 100)

    const { journalUid } = this.state
    if (journalFor(this.props)) return
    try {
      const journlReturned = await this.props.getJournal(journalUid)
      if (!journlReturned) {
        this.props.history.goBack()
      }
      if (!this._isMounted) return
      this.setState({
        privacy: journlReturned ? journlReturned.privacy : 'PRIVATE',
        markedAs: journlReturned ? journlReturned.markedAs : '',
        journalContent: journlReturned ? journlReturned.content : '',
        journalLovedCount: journlReturned ? journlReturned.lovedCount : 0,
        journalCommentCount: journlReturned ? journlReturned.commentCount : 0,
        journalWordCount: journlReturned ? journlReturned.journalWordCount : 0,
        journalWordCountBeforeEditing: journlReturned
          ? journlReturned.journalWordCount
          : 0,
        journalBookId: journlReturned
          ? journlReturned.journalBookId || journlReturned.publicJournalBookId
          : '',
        bookChangedFromId: journlReturned
          ? journlReturned.journalBookId || journlReturned.publicJournalBookId
          : '',
        journalCreatedAt: journlReturned
          ? new Date(journlReturned.createdAt)
          : '',
      })
    } catch (err) {
      console.log(err)
    }
  }

  _selectPrivacy = async (e) => {
    this.setState({
      privacy: e.target.value,
    })
  }

  _popupControl = async (callback) => {
    this.setState({
      showUpgrade: callback,
    })
  }

  _isPaid = () => isPaidUser(this.props.user.currentUser.attributes['custom:membership'])

  _handleChange = (textPassed, wordCount) => {
    const { currentJournal } = this.props.journal
    if (currentJournal === textPassed) return
    this.latest = { content: textPassed, journalWordCount: wordCount }
    if (this._isMounted) {
      this.setState({
        journalContent: textPassed,
        journalWordCount: wordCount,
      })
    }
    // While publishing, the text only goes into this.latest (Publish sends it).
    if (textPassed.length <= 4 || this.saves.closed) return

    // if non-paid user, disable autosave and shows the warning message for the first time only
    // TODO: remove auto save warning message on next major update.
    if (!this._isPaid()) {
      // Shown once; never used up while the page is closing.
      if (this._isMounted && localStorage.getItem('auto-save-for-paid-users') !== 'true') {
        this.setState({
          showUpgrade: true,
          upgradePopUpMessage: { featureName: 'Autosave', ctaText: 'Close' },
        })
        localStorage.setItem('auto-save-for-paid-users', 'true')
      }
      return
    }
    this.saves.draft(this._saveDraft)
  }

  // Runs from the save queue, so it reads the newest text and never runs twice at once.
  _saveDraft = async () => {
    const { privacy, journalUid, journalAuthorId, journalBookId } = this.state
    await this.props.updateJournal({
      id: journalUid,
      journalAuthorId: journalAuthorId,
      privacy: this._isPaid() ? privacy : 'PRIVATE',
      draft: true,
      ...this.latest,
      journalBookId: journalBookId,
    })
    if (this.state.saveError) this.setState({ saveError: '' })
  }

  _publishJournal = async () => {
    const { privacy } = this.state
    const { user } = this.props
    if (
      privacy === 'PUBLIC' &&
      !isPaidUser(user.currentUser.attributes['custom:membership'])
    ) {
      this.setState({
        showUpgrade: true,
        upgradePopUpMessage: {
          featureName: 'Publishing a public journal',
          ctaText: 'Publish it as a private journal',
        },
      })
    } else {
      this._updateJournal(privacy)
    }
  }

  _updateJournal = async (privacy) => {
    if (this.publishing) return
    this.publishing = true
    // No more drafts (the waiting one is dropped, the one in flight finishes), then the last
    // half second of typing, which only updates this.latest now.
    const closing = this.saves.close()
    this.editor.current?.flush()
    this.setState({ isSavingJournal: true })
    await closing
    const {
      journalUid,
      journalAuthorId,
      markedAs,
      journalContent,
      journalLovedCount,
      journalCommentCount,
      journalWordCount,
      journalWordCountBeforeEditing,
      journalBookId,
      bookChangedFromId,
      journalCreatedAt,
    } = this.state
    const text = this.latest ?? { content: journalContent, journalWordCount: journalWordCount ?? countWords(journalContent) }
    if (journalUid) {
      try {
        await this.props.updateJournal({
          id: journalUid,
          privacy: privacy,
          journalAuthorId: journalAuthorId,
          draft: false,
          content: text.content,
          markedAs: markedAs,
          lovedCount: journalLovedCount,
          commentCount: journalCommentCount,
          journalWordCount: text.journalWordCount,
          journalWordCountBeforeEditing: journalWordCountBeforeEditing,
          journalBookId: journalBookId,
          createdAt: journalCreatedAt.toISOString(),
        })
        // if user changes book from one to another, subtract total number of journals in the original book
        // or no book to book
        if (journalBookId !== bookChangedFromId) {
          bookChangedFromId &&
            (await this.props.adjustBookJournalsCount(bookChangedFromId, -1))
          journalBookId &&
            (await this.props.adjustBookJournalsCount(journalBookId, 1))
        }

        this.props.history.push({
          pathname:
            (privacy === 'PUBLIC' ? '/public-journal/' : '/journal/') +
            journalUid,
          state: { prevPath: '/edit' },
        })
      } catch (err) {
        console.log(err)
        this.publishing = false
        this.saves.reopen()
        if (refusalOf(err) === MEMBERS_ONLY) {
          // The server says the membership has lapsed: offer to publish it privately instead.
          this.setState({
            isSavingJournal: false,
            saveError: '',
            showUpgrade: true,
            upgradePopUpMessage: {
              featureName: 'Publishing a public journal',
              ctaText: 'Publish it as a private journal',
            },
          })
          return
        }
        this.setState({
          isSavingJournal: false,
          saveError: publishFailedMessage(err),
        })
      }
    }
  }

  componentWillUnmount() {
    // Leaving the page: the last typing and the waiting draft are still saved.
    this._isMounted = false
    this.editor.current?.flush()
    this.saves.drain()
  }

  _selectBook = async (e) => {
    this.setState({
      journalBookId: e.target.value,
    })
  }

  _newJournalCreatedAt = (date) => {
    const { journalCreatedAt } = this.state
    const { user } = this.props
    if (!isPaidUser(user.currentUser.attributes['custom:membership'])) {
      this.setState({
        showUpgrade: true,
        journalCreatedAt: journalCreatedAt,
        upgradePopUpMessage: {
          featureName: 'Changing date and time',
          ctaText: 'Publish it with existing date and time',
        },
      })
    } else {
      this.setState({
        journalCreatedAt: date,
      })
    }
  }

  render() {
    const {
      privacy,
      showUpgrade,
      journalBookId,
      upgradePopUpMessage,
      journalCreatedAt,
      isSavingJournal,
      saveError,
    } = this.state
    const currentJournal = journalFor(this.props)
    const { journal, user } = this.props

    const editJoural = !currentJournal ? (
      <LoadingCard />
    ) : (
      <QuillEditor
        ref={this.editor}
        readOnly={false}
        value={currentJournal.content}
        textCallback={this._handleChange}
      />
    )

    const bookList =
      user && user.userBooks.length > 0 ? (
        <select
          className="select-new book-select"
          onChange={this._selectBook}
          defaultValue={journalBookId ? journalBookId : ''}
          id="editJournal_book_select"
        >
          <option value="">No book</option>
          {user.userBooks.map((book) => {
            return (
              <option key={book.id} value={book.id}>
                {book.book}
              </option>
            )
          })}
        </select>
      ) : (
        <select className="select-new book-select">
          <option value="">No book</option>
        </select>
      )

    return (
      <div className="write-journal">
        <ul className="sub-nav">
          <li>
            <button
              onClick={() => this.props.history.goBack()}
              className="link-btn"
              id="editJournal_cancel_edit"
            >
              <ion-icon name="close"></ion-icon> Cancel
            </button>
          </li>
          <li>{bookList}</li>
          <li>
            {currentJournal ? (
              <button
                onClick={this._publishJournal}
                id="editJournal_update_button"
                className="link-btn"
                disabled={
                  journal.pendingCreate ||
                  journal.pendingUpdate ||
                  isSavingJournal
                    ? true
                    : false
                }
              >
                {journal.pendingCreate ||
                journal.pendingUpdate ||
                isSavingJournal
                  ? 'Updating...'
                  : 'Update'}
              </button>
            ) : (
              ''
            )}
          </li>
        </ul>
        <div className="journal-option">
          <DateTimePicker
            setDate={journalCreatedAt}
            newDate={this._newJournalCreatedAt}
          />
          {currentJournal ? (
            <select
              className="select-new"
              onChange={this._selectPrivacy}
              value={privacy}
              id="editJournal_privacy_select"
            >
              <option
                value="PRIVATE"
                defaultValue={privacy === 'PRIVATE' ? 'selected' : ''}
              >
                Private
              </option>
              <option
                value="PUBLIC"
                defaultValue={privacy === 'PUBLIC' ? 'selected' : ''}
              >
                Public
              </option>
            </select>
          ) : (
            ''
          )}
        </div>
        {saveError && (
          <p className="error-msg" role="alert">
            {saveError}
          </p>
        )}
        <div className="Container write-editor">{editJoural}</div>
        <UpgradePopup
          featureName={upgradePopUpMessage.featureName}
          ctaText={upgradePopUpMessage.ctaText}
          showUpgrade={showUpgrade}
          popupControl={this._popupControl}
          publishPrivate={this._updateJournal}
        />
      </div>
    )
  }
}

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      updateJournal,
      getJournal,
      adjustBookJournalsCount,
    },
    dispatch
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(EditJournal)
